// components/AssignModal.js
import {
  Modal,
  Checkbox,
  Select,
  Button,
  message as Message,
  Spin,
  Popconfirm,
  Row,
  Col,
  Typography,
  Space,
  Tag,
} from "antd";
import { useState, useEffect } from "react";
import axiosInstance from "../config/axiosInstance";
import { webAPIs } from "../config/webAPIs";

const { Text } = Typography;

export default function AssignModal({
  visible,
  onClose,
  files,
  attributes,
  projectId,
  onAssigned,
}) {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [selectedAttribute, setSelectedAttribute] = useState(null);
  const [selectedLevel, setSelectedLevel] = useState(null);
  const [levels, setLevels] = useState([]);
  const [assignments, setAssignments] = useState({}); // { [documentId]: { assignmentId, attributeId, levelId, attributeName, levelValue } }
  const [loading, setLoading] = useState(false);

  // Fetch all assignments for the project and map them for easy lookup
  const fetchAssignments = async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const { data } = await axiosInstance.get(
        `${webAPIs.listAttributes}?projectId=${projectId}`
      );
      const assignmentMap = {};
      data.forEach((attr) => {
        attr.DocumentAttributeLevels?.forEach((level) => {
          level.DocumentAttributeAssignments?.forEach((assignment) => {
            if (!assignmentMap[assignment.documentId]) {
              assignmentMap[assignment.documentId] = [];
            }
            assignmentMap[assignment.documentId].push({
              assignmentId: assignment.id,
              attributeId: attr.id,
              levelId: level.id,
              attributeName: attr.name,
              levelValue: level.value,
            });
          });
        });
      });
      setAssignments(assignmentMap);
    } catch {
      Message.error("Could not fetch existing assignments.");
      setAssignments({});
    } finally {
      setLoading(false);
    }
  };

  // Reset state on close and fetch assignments on open
  useEffect(() => {
    if (visible) {
      fetchAssignments();
    }
  }, [visible, projectId]);

  // When a SINGLE file is selected, pre-fill the form with its first assignment for quick editing
  useEffect(() => {
    if (selectedFiles.length === 1) {
      const docId = selectedFiles[0];
      const docAssignments = assignments[docId] || [];
      if (docAssignments.length > 0) {
        setSelectedAttribute(docAssignments[0].attributeId);
        setSelectedLevel(docAssignments[0].levelId);
      } else {
        setSelectedAttribute(null);
        setSelectedLevel(null);
      }
    } else {
      setSelectedAttribute(null);
      setSelectedLevel(null);
    }
  }, [selectedFiles, assignments]);

  // Update available levels when the attribute changes
  useEffect(() => {
    if (selectedAttribute) {
      const attr = attributes.find((a) => a.id === selectedAttribute);
      setLevels(attr ? attr.DocumentAttributeLevels : []);
    } else {
      setLevels([]);
    }
    setSelectedLevel(null); // Reset level selection when attribute changes
  }, [selectedAttribute, attributes]);

  // Assign or Update the selected attribute/level for all selected files
  const handleAssign = async () => {
    if (selectedFiles.length === 0) {
      Message.error("Please select at least one file.");
      return;
    }
    if (!selectedAttribute || !selectedLevel) {
      Message.error("Please select both an attribute and a level.");
      return;
    }
    setLoading(true);
    try {
      const promises = selectedFiles.map(async (documentId) => {
        const docAssignments = assignments[documentId] || [];
        // Check if this document already has this attribute assigned
        const existing = docAssignments.find(
          (a) => a.attributeId === selectedAttribute
        );
        if (existing) {
          // Update the level for this attribute (PUT)
          return axiosInstance.put(
            webAPIs.updateAssignment(existing.assignmentId),
            { levelId: selectedLevel }
          );
        } else {
          // Add a new assignment (POST)
          return axiosInstance.post(webAPIs.assignAttributeLevel, {
            documentId,
            attributeId: selectedAttribute,
            levelId: selectedLevel,
          });
        }
      });
      await Promise.all(promises);
      Message.success("Assignment(s) saved successfully!");
      if (onAssigned) onAssigned();
      onClose();
    } catch (e) {
      console.error("Assignment failed:", e);
      Message.error("An error occurred while saving the assignment(s).");
      fetchAssignments();
    } finally {
      setLoading(false);
    }
  };

  // Remove a specific assignment from a document
  const handleRemoveSingleAssignment = async (documentId, assignmentId) => {
    setLoading(true);
    try {
      await axiosInstance.delete(webAPIs.deleteAssignment(assignmentId));
      Message.success("Assignment removed successfully!");
      if (onAssigned) onAssigned();
      fetchAssignments();
    } catch (e) {
      console.error("Failed to remove assignment:", e);
      Message.error("An error occurred while removing the assignment.");
      fetchAssignments();
    } finally {
      setLoading(false);
    }
  };

  // Helper to display assignment status as visual tags (all assignments, each with a remove button)
  const getAssignmentInfo = (fileId) => {
    const docAssignments = assignments[fileId] || [];
    if (docAssignments.length === 0) return <Tag>No assignment</Tag>;
    return docAssignments.map((assignment) => (
      <Tag
        color="blue"
        key={assignment.assignmentId}
        closable
        onClose={(e) => {
          e.preventDefault();
          handleRemoveSingleAssignment(fileId, assignment.assignmentId);
        }}
        style={{ marginRight: 4 }}
      >
        {`${assignment.attributeName} / ${assignment.levelValue}`}
      </Tag>
    ));
  };

  // Determine if the remove button should be enabled (any assignment exists for selected files)
  const canRemove = selectedFiles.some(
    (id) => assignments[id] && assignments[id].length > 0
  );

  // Remove assignments from all selected files (all assignments for each file)
  const handleRemoveAssignment = async () => {
    const filesWithAssignments = selectedFiles.filter(
      (id) => assignments[id] && assignments[id].length > 0
    );
    if (filesWithAssignments.length === 0) {
      Message.error("None of the selected files have an assignment to remove.");
      return;
    }
    setLoading(true);
    try {
      // Remove all assignments for each selected file
      const promises = filesWithAssignments.flatMap((documentId) =>
        assignments[documentId].map((assignment) =>
          axiosInstance.delete(
            webAPIs.deleteAssignment(assignment.assignmentId)
          )
        )
      );
      await Promise.all(promises);
      Message.success("Assignment(s) removed successfully!");
      if (onAssigned) onAssigned();
      onClose();
    } catch (e) {
      console.error("Failed to remove assignment:", e);
      Message.error("An error occurred while removing the assignment(s).");
      fetchAssignments();
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      footer={null}
      title="Assign Attribute Level to Documents"
      width={750}
      destroyOnClose
    >
      <Spin spinning={loading} tip="Processing...">
        <Row gutter={32} style={{ marginTop: 24 }}>
          {/* Files Column */}
          <Col span={12}>
            <Space direction="vertical" style={{ width: "100%" }}>
              <Text strong>Files</Text>
              <div
                style={{
                  maxHeight: 300,
                  overflowY: "auto",
                  border: "1px solid #d9d9d9",
                  borderRadius: 6,
                  padding: "8px 12px",
                }}
              >
                <Checkbox.Group
                  value={selectedFiles}
                  onChange={setSelectedFiles}
                  style={{ display: "flex", flexDirection: "column" }}
                >
                  {files.map((f) => (
                    <Checkbox
                      value={f.id}
                      key={f.id}
                      style={{ margin: "4px 0" }}
                    >
                      <Space>
                        <Text
                          style={{ maxWidth: 200 }}
                          ellipsis={{ tooltip: f.originalName }}
                        >
                          {f.originalName}
                        </Text>
                        {getAssignmentInfo(f.id)}
                      </Space>
                    </Checkbox>
                  ))}
                </Checkbox.Group>
              </div>
            </Space>
          </Col>

          {/* Assignment Column */}
          <Col span={12}>
            <Space direction="vertical" style={{ width: "100%" }} size="large">
              <div>
                <Text strong>Attribute</Text>
                <Select
                  value={selectedAttribute}
                  onChange={setSelectedAttribute}
                  options={attributes.map((a) => ({
                    label: a.name,
                    value: a.id,
                  }))}
                  style={{ width: "100%", marginTop: 4 }}
                  placeholder="Select attribute"
                  allowClear
                />
              </div>
              <div>
                <Text strong>Level</Text>
                <Select
                  value={selectedLevel}
                  onChange={setSelectedLevel}
                  options={levels.map((l) => ({ label: l.value, value: l.id }))}
                  style={{ width: "100%", marginTop: 4 }}
                  placeholder="Select level"
                  allowClear
                  disabled={!selectedAttribute}
                />
              </div>

              <Space
                direction="vertical"
                style={{ width: "100%", paddingTop: 24 }}
              >
                <Button
                  className="bg-[#007bff] text-white"
                  onClick={handleAssign}
                  block
                  disabled={
                    !selectedAttribute ||
                    !selectedLevel ||
                    selectedFiles.length === 0
                  }
                >
                  Assign
                </Button>
                <Popconfirm
                  title="Remove assignment(s)?"
                  description="This will unassign the attribute from all selected documents that have one."
                  onConfirm={handleRemoveAssignment}
                  okText="Yes, Remove"
                  cancelText="No"
                  disabled={!canRemove}
                >
                  <Button
                    className={
                      canRemove
                        ? "text-white bg-red-600 rounded-md"
                        : "text-white rounded-md"
                    }
                    block
                    disabled={!canRemove}
                  >
                    Remove Assignment
                  </Button>
                </Popconfirm>
              </Space>

              <Text
                type="secondary"
                style={{ fontSize: 12, marginTop: 12, lineHeight: "1.4" }}
              >
                Select files to begin. Applying an assignment will overwrite any
                existing assignment on the selected files.
              </Text>
            </Space>
          </Col>
        </Row>
      </Spin>
    </Modal>
  );
}
